import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  signUp, 
  signIn, 
  signOutUser, 
  onAuthStateChange, 
  getUserProfile,
  UserProfile
} from '../services/authService';

export interface SerializableUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
}

interface AuthState {
  user: SerializableUser | null;
  userProfile: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
}

const initialState: AuthState = {
  user: null,
  userProfile: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  isInitialized: false,
};

export const signUpUser = createAsyncThunk(
  'auth/signUp',
  async ({ email, password }: {
    email: string;
    password: string;
  }, { rejectWithValue }) => {
    try {
      const userProfile = await signUp(email, password);
      return userProfile;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const signInUser = createAsyncThunk(
  'auth/signIn',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const userProfile = await signIn(email, password);
      return userProfile;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const signOutUserAsync = createAsyncThunk(
  'auth/signOut',
  async (_, { rejectWithValue }) => {
    try {
      await signOutUser();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { dispatch }) => {
    return new Promise<void>((resolve) => {
      let hasResolved = false;
      
      onAuthStateChange(async (user) => {
        if (user) {
          try {
            const userProfile = await getUserProfile(user.uid);
            const serializableUser = {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL,
              emailVerified: user.emailVerified,
            };
            dispatch(setUser({ user: serializableUser, userProfile }));
          } catch (error) {
            console.error('Error fetching user profile:', error);
            const serializableUser = {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL,
              emailVerified: user.emailVerified,
            };
            dispatch(setUser({ user: serializableUser, userProfile: null }));
          }
        } else {
          dispatch(clearUser());
        }
        
        // Only resolve on first initialization, but keep listener active
        if (!hasResolved) {
          dispatch(setInitialized());
          hasResolved = true;
          resolve();
        }
      });
      
      // Keep the auth listener active throughout the app lifecycle
      // The unsubscribe function could be stored for cleanup if needed
    });
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<{ user: SerializableUser; userProfile: UserProfile | null }>) => {
      state.user = action.payload.user;
      state.userProfile = action.payload.userProfile;
      state.isAuthenticated = true;
      state.error = null;
    },
    clearUser: (state) => {
      state.user = null;
      state.userProfile = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setInitialized: (state) => {
      state.isInitialized = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signUpUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signUpUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userProfile = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(signUpUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(signInUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signInUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userProfile = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(signInUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(signOutUserAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(signOutUserAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.userProfile = null;
        state.isAuthenticated = false;
      })
      .addCase(signOutUserAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state) => {
        state.isLoading = false;
      });
  },
});

export const { 
  setUser, 
  clearUser, 
  setLoading, 
  setError, 
  clearError, 
  setInitialized 
} = authSlice.actions;

export default authSlice.reducer; 